"use client";

/**
 * RoundCloseTimer — the closing-date countdown for the round in force.
 *
 * Reads `ROUND_ANNOUNCEMENT` from the GENERATED schedule (`roundSchedule.ts`), so the blog's
 * timer and the company's own calendar cannot disagree about a date: change the date in
 * `assumptions.json` and the timer moves with it. Nothing here is typed in by hand.
 *
 * It renders the FIRST close only. The final close is the oversubscription budget and is not
 * published, so this component has nothing to show for it — the generator never emits it.
 *
 * The countdown is computed AFTER MOUNT, never during render. A `new Date()` taken at render
 * time produces different HTML on the server than in the browser, which is a hydration
 * mismatch; starting from `null` and filling in on mount keeps the first client render
 * identical to the server's. Before that it shows the date, which is true in both.
 *
 * NO CSS CLASSES. The first version borrowed the deck's `pd-kicker` / `pd-sub` / `pd-terms`,
 * which was wrong twice over: `pd-kicker` does not exist (the deck has a `Kicker` COMPONENT),
 * and the others live in `pitchdeck.css`, which the blog page is not guaranteed to load. A
 * component that renders unstyled is worse than a component that renders plainly, so this one
 * emits semantic markup and NO class names — the host styles it through `className` or a
 * descendant selector. That also means it can be dropped anywhere without an import to satisfy.
 */

import { useEffect, useState } from "react";

import { ROUND_ANNOUNCEMENT } from "./roundSchedule";

const DAY_MS = 86_400_000;

const formatDate = (iso: string): string => {
  if (!iso) return "";
  // Parse as UTC and format in UTC so the label does not shift by a day for a reader in a
  // negative-offset timezone.
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
};

/** Whole days from today to an ISO date. Returns null before mount, and for a past date. */
const useDaysUntil = (iso: string): number | null => {
  const [days, setDays] = useState<number | null>(null);

  useEffect(() => {
    if (!iso) {
      setDays(null);
      return;
    }
    const target = new Date(`${iso}T00:00:00Z`).getTime();
    const now = Date.now();
    setDays(target > now ? Math.ceil((target - now) / DAY_MS) : null);
  }, [iso]);

  return days;
};

export interface RoundCloseTimerProps {
  /**
   * Optional class for the wrapper. The component ships NO classes of its own, so the host page
   * owns the styling — and nothing here can render unstyled because a stylesheet was absent.
   */
  className?: string;
  /** Optional class for the eyebrow line. */
  kickerClassName?: string;
}

export default function RoundCloseTimer({ className, kickerClassName }: RoundCloseTimerProps) {
  const { firstCloseAmount, firstCloseDate, hasUnpublishedCapacity } = ROUND_ANNOUNCEMENT;
  const days = useDaysUntil(firstCloseDate);

  if (!firstCloseDate) {
    // No published date: render nothing rather than an empty timer.
    return null;
  }

  return (
    <div className={className} data-round-close-timer="">
      <p className={kickerClassName}>Round open: first close</p>
      <p>
        <strong>${firstCloseAmount.toLocaleString("en-US")}</strong> closes{" "}
        <strong>{formatDate(firstCloseDate)}</strong>
        {days !== null && (
          <>
            {" "}
            <span aria-live="polite">
              ({days} {days === 1 ? "day" : "days"} left)
            </span>
          </>
        )}
      </p>
      {hasUnpublishedCapacity && (
        <p>
          The round stays open after this close. Capacity beyond it exists and we do not publish
          its amount.
        </p>
      )}
    </div>
  );
}
